import React from "react";
import DiffView from "./DiffView";
import { getLogType } from "../../../../utils/diffUtils";
import ModalPortal from "../../../../components/ModalPortal";

/**
 * ChangeDetailsModal - Modal for displaying detailed change information
 * Shows diff view or current state for rulesets, features, and families
 */
const ChangeDetailsModal = ({ isOpen, onClose, log, loading, diffEntry, rulesetData, features, families, onGoToEditor }) => {
    if (!isOpen || !log) return null;

    const typeLabel = getLogType(log.activity);
    const feature = typeLabel === 'Feature' && Array.isArray(features)
        ? features.find(f => f.id?.toString().toLowerCase() === log.targetId?.toString().toLowerCase())
        : null;
    const family = typeLabel === 'ProductFamily' && Array.isArray(families)
        ? families.find(f => f.id?.toString().toLowerCase() === log.targetId?.toString().toLowerCase())
        : null;

    const renderCurrentState = () => {
        if (typeLabel === 'RuleSet') {
            if (rulesetData) {
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0.5rem', fontSize: '0.85rem' }}>
                            <span style={{ color: 'var(--text-secondary)' }}>Created:</span>
                            <span style={{ color: 'var(--text-primary)' }}>{rulesetData.createdAt ? new Date(rulesetData.createdAt).toLocaleString() : '-'}</span>
                            <span style={{ color: 'var(--text-secondary)' }}>Last User:</span>
                            <span style={{ color: 'var(--text-primary)' }}>{rulesetData.updatedBy || 'System'}</span>
                        </div>
                        <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-primary)', marginTop: '0.5rem' }}>
                            Ruleset Matrix Table ({rulesetData.rows?.length || 0} rows)
                        </div>
                        {rulesetData.rows && rulesetData.rows.length > 0 ? (
                            <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius)', marginTop: '0.25rem' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', textAlign: 'left', minWidth: '400px' }}>
                                    <thead>
                                        <tr style={{ background: 'var(--bg-secondary)', borderBottom: '2px solid var(--border)' }}>
                                            {rulesetData.headers?.map(header => (
                                                <th key={header} style={{ padding: '0.5rem 0.75rem', fontWeight: 'bold', color: 'var(--text-primary)', borderRight: '1px solid var(--border)' }}>
                                                    {header}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rulesetData.rows.map((row, idx) => (
                                            <tr key={row.rowId || idx} style={{ borderBottom: '1px solid var(--border)' }}>
                                                {rulesetData.headers?.map(header => {
                                                    const cell = row.cells?.find(c => c.featureName === header);
                                                    return (
                                                        <td key={header} style={{ padding: '0.5rem 0.75rem', color: 'var(--text-secondary)', borderRight: '1px solid var(--border)', fontFamily: 'monospace' }}>
                                                            {cell ? cell.expectedValue : '-'}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p style={{ fontStyle: 'italic', color: 'var(--text-secondary)', fontSize: '0.85rem', margin: 0 }}>
                                No rows defined in this ruleset.
                            </p>
                        )}
                    </div>
                );
            } else {
                return (
                    <div style={{ padding: '1rem', textAlign: 'center', background: 'var(--bg-secondary)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                        <p style={{ fontStyle: 'italic', color: 'var(--text-secondary)', fontSize: '0.85rem', margin: 0 }}>
                            Ruleset configuration not found or has been deleted.
                        </p>
                    </div>
                );
            }
        }

        if (typeLabel === 'Feature') {
            if (feature) {
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div style={{ background: 'var(--bg-secondary)', padding: '0.75rem 1rem', borderRadius: 'var(--radius)', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                            <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-primary)', borderBottom: '1px solid var(--border)', paddingBottom: '0.25rem' }}>Feature Configuration</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0.35rem', fontSize: '0.8rem' }}>
                                <span style={{ color: 'var(--text-secondary)' }}>Display Label:</span>
                                <span style={{ color: 'var(--text-primary)' }}>{feature.property?.displayText || '-'}</span>
                                <span style={{ color: 'var(--text-secondary)' }}>Description:</span>
                                <span style={{ color: 'var(--text-primary)' }}>{feature.property?.description || '-'}</span>
                                <span style={{ color: 'var(--text-secondary)' }}>Input Type:</span>
                                <span style={{ color: 'var(--text-primary)', textTransform: 'capitalize' }}>{feature.property?.type || 'dropdown'}</span>
                                <span style={{ color: 'var(--text-secondary)' }}>Global Scope:</span>
                                <span style={{ color: 'var(--text-primary)' }}>{feature.isGlobal ? 'Yes' : 'No (Local)'}</span>
                            </div>
                        </div>
                        <div>
                            <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-primary)', marginBottom: '0.5rem' }}>Values ({feature.values?.length || 0})</div>
                            {feature.values && feature.values.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '200px', overflowY: 'auto', paddingRight: '0.25rem' }}>
                                    {feature.values.map(val => (
                                        <div key={val.id} style={{ padding: '0.5rem 0.75rem', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: '0.8rem' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, fontFamily: 'monospace', color: 'var(--text-primary)' }}>
                                                <span>{val.value}</span>
                                                {val.displayText && <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>{val.displayText}</span>}
                                            </div>
                                            {val.description && (
                                                <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginTop: '0.25rem' }}>{val.description}</div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p style={{ fontStyle: 'italic', color: 'var(--text-secondary)', fontSize: '0.85rem', margin: 0 }}>No values defined for this feature.</p>
                            )}
                        </div>
                    </div>
                );
            } else {
                return (
                    <div style={{ padding: '1rem', textAlign: 'center', background: 'var(--bg-secondary)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                        <p style={{ fontStyle: 'italic', color: 'var(--text-secondary)', fontSize: '0.85rem', margin: 0 }}>
                            Feature details not found or has been deleted.
                        </p>
                    </div>
                );
            }
        }

        if (typeLabel === 'ProductFamily') {
            if (family) {
                const famRulesets = family.ruleSets || [];
                const famFeatures = family.features || [];
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0.5rem', fontSize: '0.85rem' }}>
                            <span style={{ color: 'var(--text-secondary)' }}>Created:</span>
                            <span style={{ color: 'var(--text-primary)' }}>{family.createdAt ? new Date(family.createdAt).toLocaleString() : '-'}</span>
                            <span style={{ color: 'var(--text-secondary)' }}>Last User:</span>
                            <span style={{ color: 'var(--text-primary)' }}>{family.updatedBy || 'System'}</span>
                        </div>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '0.5rem' }}>
                            <div>
                                <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-primary)', marginBottom: '0.5rem' }}>Mapped Features ({famFeatures.length})</div>
                                {famFeatures.length > 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', maxHeight: '180px', overflowY: 'auto' }}>
                                        {famFeatures.map(feat => (
                                            <div key={feat.id} style={{ padding: '0.35rem 0.5rem', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                                {feat.name}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p style={{ fontStyle: 'italic', color: 'var(--text-secondary)', fontSize: '0.75rem', margin: 0 }}>No features mapped.</p>
                                )}
                            </div>
                            <div>
                                <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-primary)', marginBottom: '0.5rem' }}>Assigned Rulesets ({famRulesets.length})</div>
                                {famRulesets.length > 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', maxHeight: '180px', overflowY: 'auto' }}>
                                        {famRulesets.map(rs => (
                                            <div key={rs.id} style={{ padding: '0.35rem 0.5rem', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: '0.75rem', fontWeight: 600 }}>
                                                {rs.name}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p style={{ fontStyle: 'italic', color: 'var(--text-secondary)', fontSize: '0.75rem', margin: 0 }}>No rulesets assigned.</p>
                                )}
                            </div>
                        </div>
                    </div>
                );
            } else {
                return (
                    <div style={{ padding: '1rem', textAlign: 'center', background: 'var(--bg-secondary)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                        <p style={{ fontStyle: 'italic', color: 'var(--text-secondary)', fontSize: '0.85rem', margin: 0 }}>
                            Family details not found or has been deleted.
                        </p>
                    </div>
                );
            }
        }

        return null;
    };

    return (
        <ModalPortal>
        <div className="modal-overlay fade-in" onClick={onClose}>
            <div className="modal-content" style={{ maxWidth: '1100px', width: '95%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', padding: '1.5rem' }} onClick={e => e.stopPropagation()}>
                <header style={{ borderBottom: '1px solid var(--border)', paddingBottom: '1rem', marginBottom: '1rem', flexShrink: 0 }}>
                    <h2 style={{ color: 'var(--text-primary)', margin: 0, fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <i className={log.icon || 'fa-solid fa-circle-info'} style={{ color: log.color || 'var(--accent)' }}></i>
                        Change Details: {log.item}
                    </h2>
                    <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                        Activity: <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{log.activity}</span> &bull; By: <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{log.user}</span> &bull; {log.date ? new Date(log.date).toLocaleString() : ''}
                    </p>
                </header>

                <div style={{ flex: 1, overflowY: 'auto', paddingRight: '0.5rem' }}>
                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem' }}>
                            <i className="fa-solid fa-circle-notch fa-spin" style={{ fontSize: '1.5rem', color: 'var(--accent)', marginRight: '0.5rem' }}></i>
                            <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Loading change details...</span>
                        </div>
                    ) : diffEntry ? (
                        <div>
                            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
                                <i className={diffEntry.isHistorical ? "fa-solid fa-clock-rotate-left" : "fa-solid fa-code-compare"} style={{ marginRight: '0.35rem' }}></i>
                                {diffEntry.isHistorical ? `Historical Changes (Commit: ${diffEntry.commitComment || 'Merged'})` : 'Pending Draft Changes'}
                            </div>
                            <DiffView diff={diffEntry} />
                        </div>
                    ) : (
                        <div>
                            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
                                <i className="fa-solid fa-database" style={{ marginRight: '0.35rem' }}></i> Current System State
                            </div>
                            {renderCurrentState()}
                        </div>
                    )}
                </div>

                <div className="modal-actions" style={{ borderTop: '1px solid var(--border)', paddingTop: '1rem', marginTop: '1rem', flexShrink: 0 }}>
                    <button type="button" className="btn-secondary" onClick={onClose}>Close</button>
                    {onGoToEditor && (typeLabel === 'RuleSet' || typeLabel === 'Feature') && (
                        <button
                            type="button"
                            className="btn-primary"
                            style={{ background: 'var(--accent)', color: 'white' }}
                            onClick={() => onGoToEditor(typeLabel, log.targetId)}
                        >
                            <i className="fa-solid fa-arrow-right-to-bracket" style={{ marginRight: '0.35rem' }}></i>
                            Go to {typeLabel === 'RuleSet' ? 'Ruleset' : 'Feature'} Editor
                        </button>
                    )}
                </div>
            </div>
        </div>
        </ModalPortal>
    );
};

export default ChangeDetailsModal;
